<template>
  <div class="step1-wrapper">
    <!-- Carte satellite en plein écran -->
    <div ref="mapEl" class="step1-map"></div>

    <!-- Panneau formulaire flottant -->
    <div class="step1-card">
      <h2 class="step1-title">Édition du projet</h2>

      <div class="form-group">
        <label class="form-label">Nom du projet</label>
        <input v-model="form.projectName" type="text" class="form-input" placeholder="Ex : Maison principale" />
      </div>

      <div class="form-group">
        <!-- 03/11/2026 (réunion avec Maxime) : particulier, agricole ou ICT (choix déplacé de l'étape 4) -->
        <label class="form-label" for="type-client">Type de client</label>
        <select id="type-client" v-model="form.typeClient" class="form-input form-select">
          <option value="Particulier">Particulier</option>
          <option value="agri">Professionnel agricole</option>
          <option value="ict">Professionnel ICT (industrie, commerce, tertiaire)</option>
        </select>
      </div>

      <div class="form-section-title">Contact Client</div>

      <div v-if="form.typeClient !== 'Particulier'" class="form-group">
        <label class="form-label">Raison sociale</label>
        <input v-model="form.raisonSociale" type="text" class="form-input" placeholder="Ex : EPG Énergie" />
      </div>

      <div class="form-row">
        <div class="form-group form-group--half">
          <label class="form-label">Nom</label>
          <input v-model="form.clientLastName" type="text" class="form-input" placeholder="Nom" />
        </div>
        <div class="form-group form-group--half">
          <label class="form-label">Prénom</label>
          <input v-model="form.clientFirstName" type="text" class="form-input" placeholder="Prénom" />
        </div>
      </div>

      <!-- e-mail sur toute la largeur : les adresses longues restent lisibles (13/10/2026) -->
      <div class="form-group">
        <label class="form-label">E-mail du client *</label>
        <input v-model="form.clientEmail" type="email" class="form-input" placeholder="Ex : client@exemple.fr" />
        <span v-if="form.clientEmail && !emailValide" class="form-hint form-hint--warn">Adresse e-mail incomplète.</span>
      </div>
      <div class="form-group champ-telephone">
        <label class="form-label">Téléphone du client</label>
        <input v-model="form.clientTelephone" type="tel" class="form-input" placeholder="Ex : 06 12 34 56 78" />
      </div>

      <div class="form-section-title">Site</div>

      <!-- Recherche d'adresse avec autocomplete Nominatim -->
      <div class="form-group address-group">
        <label class="form-label">Adresse du site</label>
        <input
          v-model="addressQuery"
          type="text"
          class="form-input"
          placeholder="Tapez une adresse..."
          @input="onAddressInput"
          @keydown.down.prevent="moveSelection(1)"
          @keydown.up.prevent="moveSelection(-1)"
          @keydown.enter.prevent="selectSuggestion(selectedSuggestionIdx)"
          @keydown.esc="closeSuggestions"
          autocomplete="off"
        />
        <div v-if="suggestions.length > 0" class="suggestions-dropdown">
          <div
            v-for="(s, idx) in suggestions"
            :key="idx"
            class="suggestion-item"
            :class="{ 'suggestion-item--active': idx === selectedSuggestionIdx }"
            @mousedown.prevent="selectSuggestion(idx)"
          >
            {{ s.display_name }}
          </div>
        </div>

        <div v-if="form.address" class="address-confirmed">
          <div><strong>Adresse du projet :</strong></div>
          <div>{{ form.address }}</div>
          <div>Latitude : {{ form.latitude }}</div>
          <div>Longitude : {{ form.longitude }}</div>
          <div class="repere-aide">Si le repère n'est pas sur le bon toit, faites-le glisser ou cliquez sur la carte à l'endroit voulu.</div>
        </div>
      </div>

      <div class="step1-actions">
        <button class="btn-annuler" @click="$emit('cancel')">Annuler</button>
        <button class="btn-valider" :disabled="!isValid" @click="submit">Valider</button>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import { mapState, mapMutations } from 'vuex'

// Correction du bug des icônes Leaflet sous webpack
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

export default {
  name: 'Step1Projet',
  data() {
    return {
      formReady: false,
      form: {
        projectName: '',
        clientType: 'Particulier',
        typeClient: 'Particulier', // Particulier | agri | ict
        raisonSociale: '',
        clientLastName: '',
        clientFirstName: '',
        clientTelephone: '',
        clientEmail: '',
        address: '',
        rue: '',
        codePostal: '',
        ville: '',
        latitude: null,
        longitude: null,
      },
      addressQuery: '',
      suggestions: [],
      selectedSuggestionIdx: -1,
      searchTimer: null,
      map: null,
      marker: null,
    }
  },
  watch: {
    form: {
      deep: true,
      handler() { if (this.formReady) this.SET_DIRTY(true) },
    },
  },
  computed: {
    ...mapState('wizard', [
      'projectName', 'clientType', 'raisonSociale', 'clientLastName', 'clientFirstName', 'clientTelephone', 'clientEmail',
      'address', 'rue', 'codePostal', 'ville', 'latitude', 'longitude', 'materiel',
    ]),
    // e-mail du client obligatoire (12/10/2026) : il sert à la demande de consentement Enedis
    emailValide() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((this.form.clientEmail || '').trim())
    },
    isValid() {
      return (
        this.form.projectName.trim().length > 0 &&
        this.form.clientLastName.trim().length > 0 &&
        this.emailValide &&
        this.form.latitude !== null
      )
    },
  },
  mounted() {
    Object.assign(this.form, {
      projectName: this.projectName,
      clientType: this.clientType,
      typeClient: this.clientType === 'Particulier' ? 'Particulier' : (this.materiel && this.materiel.typePro === 'agri' ? 'agri' : 'ict'),
      raisonSociale: this.raisonSociale,
      clientLastName: this.clientLastName,
      clientFirstName: this.clientFirstName,
      clientTelephone: this.clientTelephone || '',
      clientEmail: this.clientEmail || '',
      address: this.address,
      rue: this.rue,
      codePostal: this.codePostal,
      ville: this.ville,
      latitude: this.latitude,
      longitude: this.longitude,
    })
    if (this.address) this.addressQuery = this.address
    this.$nextTick(() => {
      this.initMap()
      this.formReady = true
    })
  },
  beforeDestroy() {
    this.SET_STEP1({ ...this.typeEnregistre(),
      projectName: this.form.projectName,
      raisonSociale: this.form.typeClient !== 'Particulier' ? this.form.raisonSociale : '',
      clientLastName: this.form.clientLastName,
      clientFirstName: this.form.clientFirstName,
      clientTelephone: this.form.clientTelephone,
      clientEmail: this.form.clientEmail,
      address: this.form.address,
      rue: this.form.rue,
      codePostal: this.form.codePostal,
      ville: this.form.ville,
      latitude: this.form.latitude,
      longitude: this.form.longitude,
    })
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    ...mapMutations('wizard', ['SET_DIRTY', 'SET_STEP1']),
    /** Type de client enregistré : particulier ou professionnel, et agricole / ICT dans le choix du matériel. */
    typeEnregistre() {
      const pro = this.form.typeClient !== 'Particulier'
      const materiel = { ...(this.materiel || {}) }
      if (pro) materiel.typePro = this.form.typeClient
      return { clientType: pro ? 'Professionnel' : 'Particulier', materiel }
    },

    initMap() {
      const lat = this.form.latitude || 46.603354
      const lng = this.form.longitude || 1.888334
      const zoom = this.form.latitude ? 17 : 6

      this.map = L.map(this.$refs.mapEl, { zoomControl: true }).setView([lat, lng], zoom)

      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: 'Tiles &copy; Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye',
          maxZoom: 20,
        }
      ).addTo(this.map)

      if (this.form.latitude && this.form.longitude) {
        this.placeMarker(this.form.latitude, this.form.longitude)
      }
      // clic sur la carte : le repère y est déplacé (retour du 30/09/2026 : corriger la position utilisée par PVGIS)
      this.map.on('click', (e) => {
        if (!this.form.address) return
        this.placeMarker(e.latlng.lat, e.latlng.lng, false)
        this.positionDepuisRepere(e.latlng)
      })
    },

    positionDepuisRepere(latlng) {
      this.form.latitude = Math.round(latlng.lat * 1000000) / 1000000
      this.form.longitude = Math.round(latlng.lng * 1000000) / 1000000
    },

    onAddressInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.selectedSuggestionIdx = -1
      const query = this.addressQuery.trim()
      if (query.length < 3) {
        this.suggestions = []
        return
      }
      this.searchTimer = setTimeout(() => {
        this.fetchSuggestions(query)
      }, 500)
    },

    fetchSuggestions(query) {
      const url =
        'https://nominatim.openstreetmap.org/search?format=json&limit=5&addressdetails=1&q=' +
        encodeURIComponent(query)
      fetch(url, { headers: { 'Accept-Language': 'fr' } })
        .then(r => r.json())
        .then(data => {
          this.suggestions = data || []
        })
        .catch(() => {
          this.suggestions = []
        })
    },

    moveSelection(dir) {
      if (this.suggestions.length === 0) return
      this.selectedSuggestionIdx = Math.max(
        -1,
        Math.min(this.suggestions.length - 1, this.selectedSuggestionIdx + dir)
      )
    },

    selectSuggestion(idx) {
      if (idx < 0 || idx >= this.suggestions.length) return
      const s = this.suggestions[idx]
      const lat = parseFloat(s.lat)
      const lng = parseFloat(s.lon)
      const address = s.display_name

      // Extraction des composants d'adresse depuis l'objet structuré Nominatim.
      // Pour les adresses rurales/lieux-dits, Nominatim place le nom dans "hamlet"
      // ou "isolated_dwelling" plutôt que "road" — on les inclut dans la rue.
      const a = s.address || {}
      const houseNumber = a.house_number || ''
      const road = a.road || a.pedestrian || a.path || a.hamlet || a.isolated_dwelling || a.quarter || a.suburb || ''
      const rue = houseNumber ? (houseNumber + ' ' + road).trim() : road
      const codePostal = a.postcode || ''
      const ville = a.city || a.town || a.village || a.municipality || ''

      this.addressQuery = address
      this.form.address = address
      this.form.rue = rue
      this.form.codePostal = codePostal
      this.form.ville = ville
      this.form.latitude = Math.round(lat * 1000000) / 1000000
      this.form.longitude = Math.round(lng * 1000000) / 1000000
      this.suggestions = []
      this.selectedSuggestionIdx = -1

      this.placeMarker(lat, lng)
    },

    closeSuggestions() {
      this.suggestions = []
      this.selectedSuggestionIdx = -1
    },

    placeMarker(lat, lng, centrer = true) {
      if (!this.map) return
      if (this.marker) {
        this.marker.setLatLng([lat, lng])
      } else {
        // repère déplaçable : on le pose sur le toit exact du projet
        this.marker = L.marker([lat, lng], { draggable: true, title: 'Déplacez le repère sur le toit du projet' }).addTo(this.map)
        this.marker.on('dragend', () => this.positionDepuisRepere(this.marker.getLatLng()))
      }
      if (centrer) this.map.setView([lat, lng], 17)
    },

    submit() {
      if (!this.isValid) return
      this.SET_STEP1({ ...this.typeEnregistre(),
        projectName: this.form.projectName,
        raisonSociale: this.form.typeClient !== 'Particulier' ? this.form.raisonSociale : '',
        clientLastName: this.form.clientLastName,
        clientFirstName: this.form.clientFirstName,
        clientTelephone: this.form.clientTelephone,
        clientEmail: this.form.clientEmail,
        address: this.form.address,
        rue: this.form.rue,
        codePostal: this.form.codePostal,
        ville: this.form.ville,
        latitude: this.form.latitude,
        longitude: this.form.longitude,
      })
      this.$emit('next')
    },
  },
}
</script>

<style scoped>
.repere-aide { margin-top: 6px; font-size: .85rem; color: #666; }
.step1-wrapper {
  position: relative;
  width: 100%;
  height: calc(100vh - 140px); /* 80px menu + 60px barre de progression */
}

.step1-map {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.step1-card {
  position: absolute;
  top: 76px; /* sous la barre des étapes (13/10/2026 : elle était recouverte) */
  left: 20px;
  width: 460px; /* assez large pour lire l'e-mail du client, seul sur sa ligne (13/10/2026) */
  max-width: calc(100% - 40px);
  z-index: 1000;
  background: #fff;
  border-radius: 12px;
  padding: 24px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  max-height: calc(100vh - 256px);
  overflow-y: auto;
}

.step1-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 18px;
}

.form-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #222;
  margin: 8px 0 8px;
  padding-bottom: 5px;
  border-bottom: 2px solid #f5c518;
  display: inline-block;
}

.form-group {
  margin-bottom: 14px;
  position: relative;
}

.form-row {
  display: flex;
  gap: 10px;
}

.champ-telephone { max-width: 220px; }

.form-group--half {
  flex: 1;
  min-width: 0;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.form-input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  color: #222;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #f5c518;
  box-shadow: 0 0 0 2px rgba(245, 197, 24, 0.2);
}

.form-select {
  appearance: none;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 10px center;
  cursor: pointer;
}

/* Suggestions d'adresse */
.address-group {
  z-index: 2000;
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 3000;
  max-height: 200px;
  overflow-y: auto;
}

.suggestion-item {
  padding: 8px 10px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  line-height: 1.4;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item--active {
  background: #fff8e1;
}

.address-confirmed {
  margin-top: 8px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 6px;
  font-size: 12px;
  color: #444;
  line-height: 1.6;
}

.address-confirmed strong {
  color: #222;
}

/* Boutons */
.step1-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.btn-annuler {
  flex: 1;
  padding: 9px 0;
  border: 1px solid #ccc;
  border-radius: 20px;
  background: #fff;
  color: #555;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s;
}

.btn-annuler:hover {
  border-color: #999;
}

.btn-valider {
  flex: 1.5;
  padding: 9px 0;
  border: none;
  border-radius: 20px;
  background: #f5c518;
  color: #222;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}

.btn-valider:hover:not(:disabled) {
  background: #e6b800;
}

.btn-valider:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.form-hint--warn { font-size: 12px; color: #c0392b; }
</style>
